import { ModalController, Platform } from '@ionic/angular';
import { from, Observable, Subject } from 'rxjs';
import { Geolocation } from '@awesome-cordova-plugins/geolocation/ngx';
import { Injectable } from '@angular/core';
import { Geolocation as CapGeolocation } from '@capacitor/geolocation';
import { Storage } from '@ionic/storage';
import { EstacaoService } from './estacao.service';
import { FavoritoService } from './favorito.service';
import { toast } from '../util/toast';
import { AtivarGpsComponent } from 'src/app/definicoes/ativar-gps/ativar-gps.component';

const TIMEOUT_GEOLOCATION: number = 10 * 1000;
const MAXIMUM_AGE_GEOLOCATION: number = 30 * 1000;
const TIMESTAMP_DURATION: number = 60 * 1000;
const LOCALIZACAO_ATIVADA = '_localizacaoGpsAtivada';
const SITUACAO_SELECAO_AUTOMATICA = '_selecaoAutomatica';
const LOCALIZACAO = 'monitorarLocalizacao';

@Injectable({
  providedIn: 'root'
})
export class LocalizacaoService {

  statusSelecao$ = new Subject<boolean>();

  private gpsDesabilitado = false;
  private ultimoTimestamp: number = null;
  private ultimaLatitude: number = null;
  private ultimaLongitude: number = null;
  private watchId = null;

  constructor(
    private platform: Platform,
    private geolocation: Geolocation,
    private estacaoService: EstacaoService,
    private storage: Storage,
    private modalCtrl: ModalController,
    private favoritoService: FavoritoService)
  {

  }

  localizacaoAtual(): Observable<any> {
    return from(new Promise((resolve, reject) => {
      if (this.platform.is('hybrid')) {
        this.geolocation.getCurrentPosition()
          .then(async (resp: any) => {
            await this.storage.set(LOCALIZACAO_ATIVADA, true);
            resolve({
              lat: resp.coords.latitude,
              lng: resp.coords.longitude,
              timestamp: resp.timestamp
            });
          }).catch(async e => {
            console.log(e);
            await this.storage.set(LOCALIZACAO_ATIVADA, false);
          });
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (resp) => {
            await this.storage.set(LOCALIZACAO_ATIVADA, true);
            resolve({
              lat: resp.coords.latitude,
              lng: resp.coords.longitude,
              timestamp: resp.timestamp
            });
          },
          async () => {
            await this.storage.set(LOCALIZACAO_ATIVADA, false);
            reject();
          },
          { timeout: TIMEOUT_GEOLOCATION }
        );
      }
    }));
  }

  async statusSelecaoAutomatica() {

    const statusSelecao = await this.storage.get(SITUACAO_SELECAO_AUTOMATICA);
    if (statusSelecao != null) {
      return statusSelecao;
    }
    return false;
  }

  async habilitarEstacaoProxima(latitude?, longitude?, timestamp?) {

    this.gpsDesabilitado = false;

    if (this.platform.is('android')) {
      try {
        await CapGeolocation.requestPermissions();
      } catch (error) {
        this.gpsDesabilitado = true;
      }
    }

    if (this.gpsDesabilitado) {
      this.desabilitarEstacaoProxima();
      this.abrirModalAtivarGps();
      return;
    }

    await this.storage.set(SITUACAO_SELECAO_AUTOMATICA, true);
    this.statusSelecao$.next(true);

    if (timestamp) {
      this.ultimoTimestamp = timestamp;
    }
    if (latitude && longitude) {
      this.ultimaLatitude = latitude;
      this.ultimaLongitude = longitude;
    }

    await this.startEstacaoProxima();
  }

  async desabilitarEstacaoProxima() {

    await this.storage.set(SITUACAO_SELECAO_AUTOMATICA, false);
    this.statusSelecao$.next(false);

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
    }

    this.ultimoTimestamp = null;
    this.ultimaLatitude = null;
    this.ultimaLongitude = null;
  }

  async startEstacaoProxima() {

    const habilitado = await this.statusSelecaoAutomatica();
    if (!habilitado) {
      return;
    }

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
    }

    const watchOptions = { maximumAge: MAXIMUM_AGE_GEOLOCATION, timeout: TIMEOUT_GEOLOCATION };

    this.watchId = navigator.geolocation.watchPosition((resp: any) => {

      if (this.ultimoTimestamp === null) {
        this.ultimoTimestamp = resp.timestamp;
      }

      if (this.ultimaLatitude === null || this.ultimaLongitude === null) {
        this.verificarEstacaoProxima(resp.coords.latitude, resp.coords.longitude);
      } else {
        if (this.ultimoTimestamp + TIMESTAMP_DURATION < resp.timestamp) {
          this.ultimoTimestamp = resp.timestamp;

          if (this.ultimaLatitude !== resp.coords.latitude || this.ultimaLongitude !== resp.coords.longitude) {
            this.verificarEstacaoProxima(resp.coords.latitude, resp.coords.longitude);
          }
        }
      }
    }, error => {
      console.log(error);
      if (error.code === 1 && !this.gpsDesabilitado) {
        this.gpsDesabilitado = true;

        this.desabilitarEstacaoProxima();
        this.abrirModalAtivarGps();
      } else {
        toast('Não foi possível recuperar a sua localização');
      }
    }, watchOptions);
  }

  private async abrirModalAtivarGps() {

    const popover = await this.modalCtrl.create({
      component: AtivarGpsComponent,
      cssClass: 'modal-ativar-gps'
    });
    return await popover.present();
  }

  private verificarEstacaoProxima(lat, lng) {

    this.ultimaLatitude = lat;
    this.ultimaLongitude = lng;

    this.estacaoService.estacaoMaisProxima(lat, lng).subscribe(estacao => {
      if (estacao) {
        this.favoritoService.trocarEstacaoPrincipal(estacao);
      }
    }, error => {
      console.log(error);
    });
  }

  async setLocalizacao(localizacao: any): Promise<any> {
    const obj = JSON.stringify(localizacao);

    return new Promise((resolve, reject) => {
      localStorage.setItem(LOCALIZACAO, obj);
      resolve(true);
    });
  }

  async getLocalizacao() {
    return JSON.parse(localStorage.getItem(LOCALIZACAO));
  }

}
